import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';
import { formatPrice } from '../../utils/formatPrice';

const PER_PAGE = 48;

function SkeletonCard() {
  return (
    <div className="animate-pulse overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="aspect-[3/2] w-full bg-gray-200" />
      <div className="p-2 space-y-1.5">
        <div className="h-3 w-3/4 rounded bg-gray-200" />
        <div className="h-3 w-1/3 rounded bg-gray-200" />
      </div>
    </div>
  );
}

function CullCard({ product, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(product.id)}
      className={
        'group relative flex flex-col overflow-hidden rounded-lg border-2 bg-white text-left transition-all ' +
        (selected
          ? 'border-red-500 ring-2 ring-red-400/40'
          : 'border-gray-200 hover:border-gray-400')
      }
    >
      {/* Selection overlay */}
      {selected && (
        <div className="absolute inset-0 z-10 bg-red-500/25 flex items-center justify-center pointer-events-none">
          <div className="bg-red-600 rounded-full p-1.5">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-gray-100">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-300">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-2">
        <p className="line-clamp-2 text-xs font-medium leading-snug text-gray-900">
          {product.name}
        </p>
        <p className="mt-0.5 text-xs font-semibold text-gray-700">
          {formatPrice(parseFloat(product.price) || 0)}
        </p>
      </div>
    </button>
  );
}

function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  const btnBase = 'inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded px-1.5 text-sm font-medium transition-colors focus:outline-none';
  return (
    <nav className="flex items-center justify-center gap-1">
      <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}
        className={`${btnBase} ${page <= 1 ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      {pages.map((p, idx) =>
        p === '...' ? (
          <span key={`e-${idx}`} className="px-1 text-gray-400">&hellip;</span>
        ) : (
          <button key={p} type="button" onClick={() => onPageChange(p)}
            className={`${btnBase} ${p === page ? 'bg-pos-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {p}
          </button>
        )
      )}
      <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}
        className={`${btnBase} ${page >= totalPages ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </nav>
  );
}

export default function ProductCull({ onClose }) {
  const [skuPrefix, setSkuPrefix] = useState('DF-GAL-');
  const [skuInput, setSkuInput] = useState('DF-GAL-');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [selected, setSelected] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ done: 0, total: 0 });
  const [fetchTick, setFetchTick] = useState(0);

  const searchTimer = useRef(null);
  const skuTimer = useRef(null);

  // Load categories once
  useEffect(() => {
    api.getCategories().then(setCategories).catch(() => {});
  }, []);

  // Fetch products when filters/page/tick change
  useEffect(() => {
    setLoading(true);
    api.getProducts({ search, category, skuPrefix, page, per_page: PER_PAGE })
      .then((res) => {
        setProducts(res.products || []);
        setTotal(res.total || 0);
        setTotalPages(res.total_pages || 0);
      })
      .catch(() => {
        setProducts([]);
        setTotal(0);
        setTotalPages(0);
      })
      .finally(() => setLoading(false));
  }, [search, category, skuPrefix, page, fetchTick]);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  }, []);

  const handleSkuPrefixChange = useCallback((e) => {
    const val = e.target.value;
    setSkuInput(val);
    clearTimeout(skuTimer.current);
    skuTimer.current = setTimeout(() => { setSkuPrefix(val); setPage(1); }, 600);
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allOnPageSelected = products.length > 0 && products.every((p) => selected.has(p.id));

  const toggleSelectPage = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        products.forEach((p) => next.delete(p.id));
      } else {
        products.forEach((p) => next.add(p.id));
      }
      return next;
    });
  }, [products, allOnPageSelected]);

  const handleDelete = useCallback(async () => {
    const ids = [...selected];
    setDeleting(true);
    setDeleteProgress({ done: 0, total: ids.length });

    // Delete in parallel batches of 5
    const CONCURRENCY = 5;
    let done = 0;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const chunk = ids.slice(i, i + CONCURRENCY);
      await Promise.allSettled(chunk.map((id) => api.deleteProduct(id)));
      done += chunk.length;
      setDeleteProgress({ done, total: ids.length });
    }

    setSelected(new Set());
    setShowConfirm(false);
    setDeleting(false);
    setPage(1);
    setFetchTick((t) => t + 1);
  }, [selected]);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-pos-bg">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-pos-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-pos-foreground">Cull Products</h2>
            {!loading && (
              <span className="text-sm text-pos-muted-fg">{total.toLocaleString()} products</span>
            )}
            {selected.size > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                {selected.size} marked for removal
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <button
                onClick={() => setShowConfirm(true)}
                className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove {selected.size}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-pos-muted-fg">
            <span>SKU prefix</span>
            <input
              value={skuInput}
              onChange={handleSkuPrefixChange}
              className="w-28 rounded border border-pos-border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pos-primary"
              placeholder="e.g. DF-GAL-"
            />
          </div>

          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            className="rounded border border-pos-border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pos-primary"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>

          <input
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search name…"
            className="rounded border border-pos-border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pos-primary w-40"
          />

          <button
            type="button"
            onClick={toggleSelectPage}
            className="ml-auto rounded border border-pos-border px-2 py-1 text-xs text-pos-muted-fg hover:bg-pos-muted transition-colors"
          >
            {allOnPageSelected ? 'Deselect page' : 'Select page'}
          </button>

          {selected.size > 0 && (
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="rounded border border-pos-border px-2 py-1 text-xs text-pos-muted-fg hover:bg-pos-muted transition-colors"
            >
              Clear selection
            </button>
          )}
        </div>
      </header>

      {/* Grid */}
      <main className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
            {Array.from({ length: 24 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <svg className="mb-4 h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <p className="text-base font-medium text-gray-500">No products found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
              {products.map((p) => (
                <CullCard key={p.id} product={p} selected={selected.has(p.id)} onToggle={toggleSelect} />
              ))}
            </div>
            <div className="mt-4">
              <Pagination page={page} totalPages={totalPages} onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }} />
            </div>
          </>
        )}
      </main>

      {/* Confirm delete dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl mx-4">
            {deleting ? (
              <div className="text-center py-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">
                  Removing {deleteProgress.done} / {deleteProgress.total}…
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900 mb-1">Remove {selected.size} products?</h3>
                <p className="text-sm text-gray-500 mb-5">
                  They'll move to the WordPress bin and can be recovered from WooCommerce if needed.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground hover:bg-pos-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                  >
                    Remove {selected.size}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
